import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, dashboardPathFor } from "@/contexts/AuthContext";
import SolimoesLogo from "@/components/SolimoesLogo";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(email, password);
    setBusy(false);
    if (!r.ok) {
      toast.error(r.error || "Erro ao entrar");
      return;
    }
    toast.success(`Bem-vindo, ${r.user.name.split(" ")[0]}!`);
    const target = loc.state?.from?.pathname || dashboardPathFor(r.user.role);
    nav(target, { replace: true });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      <div className="flex items-center justify-center p-8 lg:p-16 bg-white">
        <div className="w-full max-w-md fade-up">
          <Link to="/" data-testid="login-logo-link"><SolimoesLogo className="h-14 w-auto" /></Link>
          <h1 className="mt-10 font-display text-4xl font-black text-[#0E2A5C]">Bem-vindo de volta</h1>
          <p className="mt-2 text-slate-600">Acesse seu painel — é rápido.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1.5">E-mail</label>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:border-[#2A8FE0] focus:ring-2 focus:ring-[#2A8FE0]/20 outline-none transition"
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Senha</label>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 pr-11 focus:border-[#2A8FE0] focus:ring-2 focus:ring-[#2A8FE0]/20 outline-none transition"
                  data-testid="login-password"
                />
                <button type="button" onClick={() => setShowPwd(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-50" data-testid="login-submit">
              {busy ? "Entrando..." : "Entrar"}
            </button>
          </form>

          <p className="mt-6 text-sm text-slate-600">
            Ainda não tem cartão? <Link to="/cadastro" className="text-[#2A8FE0] font-semibold hover:underline" data-testid="login-to-register">Solicitar agora</Link>
          </p>

          <div className="mt-10 rounded-2xl border border-slate-100 bg-slate-50 p-4 text-xs text-slate-600" data-testid="demo-credentials">
            <div className="font-bold text-[#0E2A5C] mb-2">Contas de demonstração:</div>
            <ul className="space-y-1 font-mono">
              <li>Gestor: gestor@solimoescard.com.br / Solimoes@2026</li>
              <li>Cliente: cliente@solimoes.com / cliente123</li>
              <li>Credenciado: credenciado@solimoes.com / credenciado123</li>
              <li>Vendedor: vendedor@solimoes.com / vendedor123</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex relative items-center justify-center p-10" style={{background:"linear-gradient(135deg, #0E2A5C 0%, #2A8FE0 100%)"}}>
        <div className="absolute inset-0 opacity-20" style={{backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)", backgroundSize:"32px 32px"}} />
        <div className="relative max-w-md text-white">
          <h2 className="font-display text-5xl font-black leading-tight">Sua saúde financeira começa aqui.</h2>
          <p className="mt-4 text-white/85">Mais de 8 mil parceiros, 3 planos sob medida e gestão completa para empresas. Tudo em um cartão.</p>
        </div>
      </div>
    </div>
  );
}
